import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth.ts';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';

declare global {
    interface Window {
        google: any;
    }
}

export const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const { login, isLoading: isAuthLoading, error: authError, clearError } = useAuthStore();
    const [isScriptLoading, setIsScriptLoading] = React.useState(true);
    const [scriptError, setScriptError] = React.useState<string | null>(null);
    const [isGoogleLoaded, setIsGoogleLoaded] = React.useState(false);
    const googleButtonRef = React.useRef<HTMLDivElement>(null);

    const initializeGoogleButton = React.useCallback(() => {
        if (!window.google || !googleButtonRef.current) return;

        try {
            window.google.accounts.id.initialize({
                client_id: '1085501127627-r1hvfalalj4a786u1kucdk46hpodt1ae.apps.googleusercontent.com',
                callback: handleCredentialResponse,
            });
            window.google.accounts.id.renderButton(
                googleButtonRef.current,
                { 
                    theme: 'filled_blue',
                    size: 'large',
                    shape: 'pill',
                    width: 250
                }
            );
            setIsScriptLoading(false);
            setScriptError(null);
        } catch (error) {
            setScriptError('Failed to initialize Google Sign-In');
            setIsScriptLoading(false);
        }
    }, []);

    React.useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => setIsGoogleLoaded(true);
        script.onerror = () => {
            setScriptError('Failed to load Google Sign-In');
            setIsScriptLoading(false);
        };
        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        };
    }, []);

    React.useEffect(() => {
        if (isGoogleLoaded && googleButtonRef.current) {
            initializeGoogleButton();
        }
    }, [isGoogleLoaded, googleButtonRef.current, initializeGoogleButton]);

    const handleCredentialResponse = async (response: any) => {
        if (!response.credential) {
            setScriptError('No credential received from Google');
            return;
        }

        try {
            const decoded = JSON.parse(atob(response.credential.split('.')[1]));
            await login(decoded, response.credential);
            navigate('/chat');
        } catch (error) {
            setScriptError('Failed to process login');
        }
    };

    const showLoading = (isScriptLoading && !isGoogleLoaded) || isAuthLoading;
    const error = scriptError || authError;

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-gray-50 to-white px-4">
            <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-2xl shadow-sm">
                <div className="flex flex-col items-center">
                    <ChatBubbleLeftRightIcon className="w-12 h-12 mb-6" />
                    <h2 className="text-center text-3xl font-extrabold text-gray-900">
                        Welcome to Twib
                    </h2>
                    <p className="mt-3 text-center text-base text-gray-600 max-w-sm">
                        Your intelligent web browsing assistant. Sign in to start exploring your browsing history in a whole new way.
                    </p>
                </div>

                <div className="mt-8">
                    {error && (
                        <div className="mb-4 p-3 rounded bg-red-50 text-red-700 text-sm text-center">
                            {error}
                            <button 
                                onClick={() => {
                                    clearError();
                                    setScriptError(null);
                                }}
                                className="ml-2 text-red-700 hover:text-red-800 underline"
                            >
                                Dismiss
                            </button>
                        </div>
                    )}
                    {showLoading ? (
                        <div className="flex justify-center">
                            <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                    ) : (
                        <div ref={googleButtonRef} className="flex justify-center" />
                    )}
                </div>

                <p className="mt-4 text-xs text-center text-gray-500">
                    By signing in, you agree to our Terms of Service and Privacy Policy
                </p>
            </div>
        </div>
    );
};
